import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
    createWorkspaceHistoryTile,
    getWorkspaceFavourites,
    getWorkspaceHistory,
    toggleWorkspaceFavourite
} from 'services/workspace.service';

const TENOR_RE = /^\d+(?:\.\d+)?[dDwWmMyY]$/;
const TENOR_UNIT_ORDER = { d: 1, w: 7, m: 30, y: 365 };
const tenorRank = (tenor = '') => {
    const m = String(tenor).toLowerCase().match(/^(\d+(?:\.\d+)?)([dwmy])$/);
    if (!m) return Number.POSITIVE_INFINITY;
    return Number(m[1]) * TENOR_UNIT_ORDER[m[2]];
};

/**
 * Aligns with plot parsePlot / URL: compact "1y2y" is fwd only when the curve is forward (e.g. command=fwcm).
 * Spaced "1y 2y" on a forward curve is still implied fwd legs; on spot it stays a spread.
 */
export const normalizeWorkspaceStructure = (value, { isFwdCurve = false } = {}) => {
    const raw = String(value || '').trim();
    if (!raw) return null;

    const lower = raw
        .replace(/,/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

    const explicitFwdTokens = lower.split(' ').filter(Boolean);
    if (explicitFwdTokens.length === 3 && TENOR_RE.test(explicitFwdTokens[0]) && explicitFwdTokens[1] === 'fwd' && TENOR_RE.test(explicitFwdTokens[2])) {
        return `${explicitFwdTokens[0]} fwd ${explicitFwdTokens[2]}`.toUpperCase();
    }

    if (isFwdCurve) {
        const adjacent = lower.match(/^(\d+(?:\.\d+)?[dwmy])(\d+(?:\.\d+)?[dwmy])$/i);
        if (adjacent) return `${adjacent[1]} fwd ${adjacent[2]}`.toUpperCase();
        const parts = lower.split(/[\s,]+/).filter(Boolean);
        if (parts.length === 2 && parts.every((t) => TENOR_RE.test(t)) && !/\bfwd\b/i.test(lower)) {
            return `${parts[0]} fwd ${parts[1]}`.toUpperCase();
        }
        if (!/\bfwd\b/i.test(lower) && parts.length >= 2 && parts.length % 2 === 0 && parts.every((t) => TENOR_RE.test(t))) {
            const fwdParts = [];
            for (let i = 0; i < parts.length; i += 2) {
                fwdParts.push(`${parts[i]} fwd ${parts[i + 1]}`);
            }
            return fwdParts.join(' ').toUpperCase();
        }
    } else {
        const adjacent = lower.match(/^(\d+(?:\.\d+)?[dwmy])(\d+(?:\.\d+)?[dwmy])$/i);
        if (adjacent) return `${adjacent[1]} ${adjacent[2]}`.toUpperCase();
        const parts = lower.split(/[\s,]+/).filter(Boolean);
        if (parts.length >= 2 && parts.every((t) => TENOR_RE.test(t))) {
            return parts.slice().sort((a, b) => tenorRank(a) - tenorRank(b)).join(' ').toUpperCase();
        }
    }

    return lower.toUpperCase();
};

export const buildWorkspaceSessionKey = ({ curveId, structure, isFwdCurve = false }) => (
    `${curveId}::${String(normalizeWorkspaceStructure(structure, { isFwdCurve }) || '').toUpperCase()}`
);

const isSameWorkspaceHistoryEntry = (a, b) => {
    if (!a || !b) return false;
    if (a.id && b.id && a.id === b.id) return true;
    if (a.sessionKey && b.sessionKey && a.sessionKey === b.sessionKey) return true;
    if (String(a.curveId || '') !== String(b.curveId || '')) return false;
    const aStruct = String(a.structure || '').trim().toUpperCase();
    const bStruct = String(b.structure || '').trim().toUpperCase();
    return Boolean(aStruct && bStruct && aStruct === bStruct);
};

export const formatWorkspaceStructureFromLegTerms = (term, { isFwdCurve = false, immStartLabels = null } = {}) => {
    const rawLegs = Array.isArray(term)
        ? term.map((t) => String(t).trim().toUpperCase()).filter(Boolean)
        : [];
    if (!rawLegs.length) return null;
    if (!isFwdCurve) return rawLegs.join(' ');

    const starts = Array.isArray(immStartLabels)
        ? immStartLabels.map((s) => String(s).trim().toUpperCase()).filter(Boolean)
        : [];
    if (starts.length) {
        let ends = rawLegs.filter((_, idx) => idx % 2 === 1);
        if (!ends.length) ends.push(rawLegs[rawLegs.length - 1]);
        const orderedStarts = starts.slice(0, ends.length);
        if (orderedStarts.length > 1 && orderedStarts.length === ends.length) {
            orderedStarts.reverse();
        }
        const fallbackStart = orderedStarts[orderedStarts.length - 1] || starts[0] || rawLegs[0];
        return ends.map((end, idx) => `${orderedStarts[idx] || fallbackStart} FWD ${end}`).join(' ');
    }

    if (rawLegs.length >= 2 && rawLegs.length % 2 === 0) {
        const pairs = [];
        for (let i = 0; i < rawLegs.length; i += 2) {
            pairs.push(`${rawLegs[i]} FWD ${rawLegs[i + 1]}`);
        }
        return pairs.join(' ');
    }
    return rawLegs.join(' ');
};

/** Build workspace history structure from IRS route legs + optional IMM label query params. */
export const buildWorkspaceStructureFromRouteLegs = ({
    legs,
    isFwdCurve = false,
    immFwd = null,
    immLabel = null,
    immLabel3 = null
} = {}) => {
    const legsRaw = Array.isArray(legs) ? legs.join(',') : String(legs || '');
    const legTokens = legsRaw
        .split(',')
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean);
    if (!legTokens.length) return null;
    const immStartLabels = [];
    if (immFwd) immStartLabels.push(String(immFwd).toUpperCase());
    if (immLabel) immStartLabels.push(String(immLabel).toUpperCase());
    if (immLabel3) immStartLabels.push(String(immLabel3).toUpperCase());
    return formatWorkspaceStructureFromLegTerms(legTokens, {
        isFwdCurve,
        immStartLabels: immStartLabels.length ? immStartLabels : null
    });
};

/** Wait until IMM labels are resolved before recording history (avoids decimal-year duplicate tiles). */
export const isWorkspaceImmStructureReady = (structure, {
    immFwd = null,
    immLabel = null,
    immLabel3 = null,
    immValue = null
} = {}) => {
    const st = String(structure || '').trim();
    if (!st) return true;
    const immUrl = Boolean(immFwd || immLabel || immLabel3 || immValue);
    if (!immUrl) return true;
    if (/\b[ZMUH]\d{1,2}\b/i.test(st)) return true;
    if (/\b\d+\.\d{3,}[dwmy]\b/i.test(st)) return false;
    return true;
};

const initialState = {
    historyTiles: [],
    favourites: [],
    favouriteKeys: {},
    nextCursor: null,
    hasMoreHistory: false,
    loadingHistory: false,
    loadingFavourites: false,
    /** 'history' | 'favourites' | null — slide-over from rail, not a route */
    workspaceSheet: null
};

let workspaceSlice = (set, get) => ({
    ...initialState,
    resetWorkspace: () => set(initialState),
    toggleWorkspaceSheet: (mode) => {
        set((state) => {
            if (state.workspaceSheet === mode) state.workspaceSheet = null;
            else state.workspaceSheet = mode;
        });
    },
    closeWorkspaceSheet: () => {
        set((state) => { state.workspaceSheet = null; });
    },
    setWorkspaceSheet: (mode) => {
        set((state) => { state.workspaceSheet = mode; });
    },
    fetchHistory: async ({ append = false } = {}) => {
        const cursor = append ? get().nextCursor : null;
        set((state) => { state.loadingHistory = true; });
        const response = await getWorkspaceHistory(cursor);
        if (!response?.status) {
            set((state) => { state.loadingHistory = false; });
            return;
        }
        const payload = response?.data?.entity || {};
        const rawItems = payload?.items || [];
        const items = append
            ? rawItems
            : [...rawItems].sort(
                (a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime()
            );
        set((state) => {
            state.historyTiles = append ? [...state.historyTiles, ...items] : items;
            state.nextCursor = payload?.nextCursor || null;
            state.hasMoreHistory = Boolean(payload?.hasMore);
            state.loadingHistory = false;
            items.forEach((item) => {
                state.favouriteKeys[item.sessionKey] = Boolean(item.isFavourite);
            });
        });
    },
    fetchFavourites: async () => {
        set((state) => { state.loadingFavourites = true; });
        const response = await getWorkspaceFavourites();
        if (!response?.status) {
            set((state) => { state.loadingFavourites = false; });
            return;
        }
        const items = response?.data?.entity?.items || [];
        set((state) => {
            state.favourites = items;
            state.loadingFavourites = false;
            items.forEach((item) => { state.favouriteKeys[item.sessionKey] = true; });
        });
    },
    toggleFavourite: async ({
        curveId,
        curveName,
        structure,
        isFwdCurve = false
    }) => {
        const curveNameSnapshot = curveName != null ? String(curveName).trim().toUpperCase() : '';
        const normalizedStructure = normalizeWorkspaceStructure(structure, { isFwdCurve });
        const response = await toggleWorkspaceFavourite({
            curveId,
            curveName: curveNameSnapshot,
            structure: normalizedStructure,
            isFwdCurve
        });
        if (!response?.status) return false;

        const next = response?.data?.entity;
        const key = next?.sessionKey || buildWorkspaceSessionKey({ curveId, structure: normalizedStructure, isFwdCurve });
        const isFavourite = Boolean(next?.isFavourite);
        set((state) => {
            state.favouriteKeys[key] = isFavourite;
            state.historyTiles = state.historyTiles.map((tile) => (
                tile.sessionKey === key ? { ...tile, isFavourite } : tile
            ));
            if (!isFavourite) {
                state.favourites = state.favourites.filter((item) => item.sessionKey !== key);
            }
        });
        if (isFavourite) {
            await get().fetchFavourites();
        }
        return isFavourite;
    },
    trackExecution: async ({
        curveId,
        curveName,
        structure,
        command,
        isFwdCurve = false
    }) => {
        if (!curveId || !curveName || !command) return null;
        const curveNameSnapshot = String(curveName).trim().toUpperCase();
        const cmdUp = String(command).toUpperCase();
        if (cmdUp === 'HIST' || cmdUp === 'FAVS') return null;
        const normalizedStructure = normalizeWorkspaceStructure(structure, { isFwdCurve });
        const response = await createWorkspaceHistoryTile({
            curveId,
            curveName: curveNameSnapshot,
            structure: normalizedStructure,
            command,
            isFwdCurve
        });
        if (!response?.status) return null;
        const tile = response?.data?.entity;
        if (!tile) return null;
        set((state) => {
            state.historyTiles = [
                tile,
                ...state.historyTiles.filter((t) => !isSameWorkspaceHistoryEntry(t, tile))
            ].sort(
                (a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime()
            );
            state.favouriteKeys[tile.sessionKey] = Boolean(tile.isFavourite);
        });
        return tile;
    }
});

workspaceSlice = immer(workspaceSlice);

const useWorkspaceStore = create(workspaceSlice);

export default useWorkspaceStore;
